import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import StatusBadge from "@/components/base/StatusBadge";
import PaymentStatusBadge from "@/components/base/PaymentStatusBadge";
import { calculateContractBalance, type ContractBalanceSummary } from "@/lib/contractBalance";

type Contract = {
  id: string;
  tenant_id: string;
  client_id: string;
  contract_number: string;
  title: string;
  event_date: string;
  event_location: string;
  package_name: string;
  total_amount: number;
  deposit_amount: number;
  balance_amount: number;
  status: string;
  signed_at: string | null;
  client_name: string;
  signature_status: string | null;
  payment_status: string;
  payment_link: string | null;
  created_at: string;
};

type Client = {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  address: string;
};

type Job = {
  id: string;
  title: string;
  job_type: string;
  status: string;
  shoot_date: string | null;
  location: string;
};

interface Props {
  contract: Contract;
  tenantId: string;
}

export default function OverviewTab({ contract, tenantId }: Props) {
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [linkedJob, setLinkedJob] = useState<Job | null>(null);
  const [loadingClient, setLoadingClient] = useState(true);
  const [loadingJob, setLoadingJob] = useState(true);
  const [balanceSummary, setBalanceSummary] = useState<ContractBalanceSummary | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(true);

  const fetchBalance = useCallback(async () => {
    setLoadingBalance(true);
    const summary = await calculateContractBalance(tenantId, contract.id, { sync: false });
    if (summary) setBalanceSummary(summary);
    setLoadingBalance(false);
  }, [tenantId, contract.id]);

  useEffect(() => {
    const fetchClient = async () => {
      if (!contract.client_id) { setLoadingClient(false); return; }
      const { data } = await supabase
        .from("clients")
        .select("id, full_name, email, phone, address")
        .eq("id", contract.client_id)
        .eq("tenant_id", tenantId)
        .maybeSingle();
      setClient(data as Client | null);
      setLoadingClient(false);
    };

    const fetchJob = async () => {
      const { data } = await supabase
        .from("jobs")
        .select("id, title, job_type, status, shoot_date, location")
        .eq("contract_id", contract.id)
        .eq("tenant_id", tenantId)
        .maybeSingle();
      setLinkedJob(data as Job | null);
      setLoadingJob(false);
    };

    fetchClient();
    fetchJob();
    fetchBalance();
  }, [contract.id, contract.client_id, tenantId, fetchBalance]);

  const formatDate = (d: string | null) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left column — 2/3 */}
      <div className="lg:col-span-2 space-y-5">

        {/* Contract summary */}
        <div className="bg-white rounded-xl border border-slate-200 rounded-xl border border-slate-200 p-5">
          <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-4">Contract Details</h3>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <div>
              <p className="text-slate-400 text-xs mb-1">Contract Number</p>
              <p className="text-[#0f172a] font-semibold text-sm">{contract.contract_number}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs mb-1">Status</p>
              <StatusBadge status={contract.status} />
            </div>
            <div>
              <p className="text-slate-400 text-xs mb-1">Event Date</p>
              <p className="text-slate-700 text-sm">{formatDate(contract.event_date)}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs mb-1">Package</p>
              <p className="text-slate-700 text-sm">{contract.package_name || "—"}</p>
            </div>
            <div className="col-span-2">
              <p className="text-slate-400 text-xs mb-1">Event Location</p>
              <p className="text-slate-700 text-sm">{contract.event_location || "—"}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs mb-1">Created</p>
              <p className="text-slate-500 text-sm">{formatDate(contract.created_at)}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs mb-1">Signature</p>
              <div className="flex items-center gap-2">
                {contract.signature_status === "signed" ? (
                  <span className="flex items-center gap-1.5 text-emerald-400 text-xs font-medium">
                    <i className="ri-checkbox-circle-fill"></i>
                    Signed {contract.signed_at ? `· ${formatDate(contract.signed_at)}` : ""}
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-slate-400 text-xs">
                    <i className="ri-time-line"></i>
                    Pending signature
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Financials */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-slate-400 text-xs uppercase tracking-wider">Financials</h3>
            <PaymentStatusBadge status={contract.payment_status} />
          </div>

          {loadingBalance ? (
            <div className="space-y-2">
              {[1,2,3,4].map((i) => (
                <div key={i} className="h-10 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : balanceSummary ? (
            <>
              {/* Breakdown rows */}
              <div className="space-y-2 mb-4">
                {/* Base contract */}
                <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3.5 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-file-text-line text-slate-400 text-xs"></i>
                    </span>
                    <span className="text-slate-600 text-sm">Base del contrato</span>
                  </div>
                  <span className="text-[#0f172a] font-semibold text-sm">
                    ${balanceSummary.baseTotal.toLocaleString()}
                  </span>
                </div>

                {/* Addon charges */}
                {balanceSummary.addonCharges.length > 0 && (
                  <div className="rounded-lg border border-[#C8A96E]/20 overflow-hidden">
                    <div className="flex items-center justify-between bg-[#C8A96E]/5 px-3.5 py-2">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-puzzle-line text-[#C8A96E] text-xs"></i>
                        </span>
                        <span className="text-slate-600 text-sm font-medium">Add-ons</span>
                      </div>
                      <span className="text-[#C8A96E] font-semibold text-sm">
                        +${balanceSummary.addonTotal.toLocaleString()}
                      </span>
                    </div>
                    {balanceSummary.addonCharges.map((charge) => (
                      <div key={charge.id} className="flex items-center justify-between px-3.5 py-1.5 border-t border-[#C8A96E]/10">
                        <span className="text-slate-400 text-xs pl-7">{charge.title}</span>
                        <span className="text-[#C8A96E] text-xs font-medium">${Number(charge.amount).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Divider + Grand Total */}
                <div className="flex items-center justify-between bg-slate-100 rounded-lg px-3.5 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-calculator-line text-slate-500 text-xs"></i>
                    </span>
                    <span className="text-slate-700 text-sm font-semibold">Total general</span>
                  </div>
                  <span className="text-[#0f172a] font-bold text-sm">
                    ${balanceSummary.grandTotal.toLocaleString()}
                  </span>
                </div>

                {/* Payments */}
                <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-3.5 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-checkbox-circle-line text-emerald-500 text-xs"></i>
                    </span>
                    <span className="text-slate-600 text-sm">Pagos recibidos</span>
                  </div>
                  <span className="text-emerald-600 font-semibold text-sm">
                    −${balanceSummary.totalPaid.toLocaleString()}
                  </span>
                </div>

                {/* Balance remaining */}
                <div className={`flex items-center justify-between rounded-lg px-3.5 py-3 border-2 ${
                  balanceSummary.balanceRemaining <= 0
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-amber-50 border-amber-200"
                }`}>
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`text-xs ${
                        balanceSummary.balanceRemaining <= 0
                          ? "ri-check-double-line text-emerald-500"
                          : "ri-time-line text-amber-500"
                      }`}></i>
                    </span>
                    <span className={`text-sm font-bold ${
                      balanceSummary.balanceRemaining <= 0 ? "text-emerald-700" : "text-amber-700"
                    }`}>
                      Balance pendiente
                    </span>
                  </div>
                  <span className={`font-bold text-lg ${
                    balanceSummary.balanceRemaining <= 0 ? "text-emerald-600" : "text-amber-500"
                  }`}>
                    ${balanceSummary.balanceRemaining.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Progress bar */}
              {(() => {
                const pct = balanceSummary.grandTotal > 0
                  ? Math.min(100, Math.round((balanceSummary.totalPaid / balanceSummary.grandTotal) * 100))
                  : 0;
                return (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-slate-400 text-xs">Progreso de pago</span>
                      <span className="text-slate-500 text-xs font-medium">{pct}% pagado</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, background: pct === 100 ? "#34d399" : "#C8A96E" }}
                      />
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            /* Fallback: original 3-col cards */
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-slate-50 rounded-lg p-3.5 text-center">
                <p className="text-slate-400 text-xs mb-1.5">Total</p>
                <p className="text-[#0f172a] font-bold text-xl">${Number(contract.total_amount).toLocaleString()}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3.5 text-center">
                <p className="text-slate-400 text-xs mb-1.5">Deposit</p>
                <p className="text-[#C8A96E] font-bold text-xl">${Number(contract.deposit_amount).toLocaleString()}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3.5 text-center">
                <p className="text-slate-400 text-xs mb-1.5">Balance</p>
                <p className="text-amber-400 font-bold text-xl">${Number(contract.balance_amount).toLocaleString()}</p>
              </div>
            </div>
          )}

          {/* Payment link */}
          {contract.payment_link && (
            <div className="mt-4 flex items-center gap-3 bg-slate-50 rounded-lg px-4 py-3">
              <div className="w-7 h-7 flex items-center justify-center bg-[#C8A96E]/10 rounded-lg flex-shrink-0">
                <i className="ri-link-m text-[#C8A96E] text-sm"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-slate-400 text-xs">Payment Link</p>
                <p className="text-slate-600 text-xs truncate">{contract.payment_link}</p>
              </div>
              <a
                href={contract.payment_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-[#C8A96E] transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-sm"></i>
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Right column — 1/3 */}
      <div className="space-y-5">

        {/* Client info */}
        <div className="bg-white rounded-xl border border-slate-200 rounded-xl border border-slate-200 p-5">
          <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-4">Client</h3>
          {loadingClient ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-4 bg-white/5 rounded animate-pulse" />
              ))}
            </div>
          ) : client ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#C8A96E]/15 flex-shrink-0">
                  <span className="text-[#C8A96E] font-semibold text-sm">
                    {client.full_name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-[#0f172a] font-medium text-sm">{client.full_name}</p>
                  <p className="text-slate-400 text-xs">Client</p>
                </div>
              </div>
              {client.email && (
                <a
                  href={`mailto:${client.email}`}
                  className="flex items-center gap-2 text-slate-500 hover:text-[#0f172a] text-xs transition-colors"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-slate-400">
                    <i className="ri-mail-line"></i>
                  </span>
                  {client.email}
                </a>
              )}
              {client.phone && (
                <a
                  href={`tel:${client.phone}`}
                  className="flex items-center gap-2 text-slate-500 hover:text-[#0f172a] text-xs transition-colors"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-slate-400">
                    <i className="ri-phone-line"></i>
                  </span>
                  {client.phone}
                </a>
              )}
              {client.address && (
                <div className="flex items-start gap-2 text-slate-400 text-xs">
                  <span className="w-4 h-4 flex items-center justify-center text-slate-400 mt-0.5">
                    <i className="ri-map-pin-line"></i>
                  </span>
                  {client.address}
                </div>
              )}
              <button
                onClick={() => navigate(`/admin/clients?id=${client.id}`)}
                className="w-full mt-1 text-xs text-slate-400 hover:text-[#C8A96E] border border-slate-200 hover:border-[#C8A96E]/30 rounded-lg py-2 transition-all cursor-pointer"
              >
                View Client Profile →
              </button>
            </div>
          ) : (
            <p className="text-slate-400 text-sm">{contract.client_name || "No client linked"}</p>
          )}
        </div>

        {/* Linked Job */}
        <div className="bg-white rounded-xl border border-slate-200 rounded-xl border border-slate-200 p-5">
          <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-4">Linked Job</h3>
          {loadingJob ? (
            <div className="h-4 bg-white/5 rounded animate-pulse" />
          ) : linkedJob ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-500/10 flex-shrink-0">
                  <i className="ri-camera-3-line text-violet-400 text-sm"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#0f172a] text-sm font-medium truncate">{linkedJob.title}</p>
                  <p className="text-slate-400 text-xs capitalize">{linkedJob.job_type}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-slate-400 mb-0.5">Status</p>
                  <StatusBadge status={linkedJob.status} />
                </div>
                {linkedJob.shoot_date && (
                  <div>
                    <p className="text-slate-400 mb-0.5">Shoot Date</p>
                    <p className="text-slate-600">{formatDate(linkedJob.shoot_date)}</p>
                  </div>
                )}
              </div>
              {linkedJob.location && (
                <p className="text-slate-400 text-xs flex items-center gap-1.5">
                  <i className="ri-map-pin-line"></i>
                  {linkedJob.location}
                </p>
              )}
              <button
                onClick={() => navigate(`/admin/jobs?id=${linkedJob.id}`)}
                className="w-full text-xs text-slate-400 hover:text-violet-400 border border-slate-200 hover:border-violet-400/30 rounded-lg py-2 transition-all cursor-pointer"
              >
                View Job →
              </button>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 mx-auto mb-2">
                <i className="ri-camera-3-line text-slate-300 text-sm"></i>
              </div>
              <p className="text-slate-400 text-xs">No job linked yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
